import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Play, ArrowRight, Quote, Mic, Smartphone } from "lucide-react";
import { api } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import useSEO from "@/hooks/useSEO";
import { Reveal, VideoRow, AccessBadge } from "@/components/ui-lux";
import PartnerCards from "@/components/PartnerCards";
import { NewsletterForm } from "@/components/Footer";

const PILLARS = ["Mindset", "Faith", "Purpose", "Relationships", "Stewardship", "Leadership", "Victory"];

const Hero = ({ home, featured }) => (
  <section className="pt-[116px] px-3 sm:px-6 lg:px-[8vw] bg-black" data-testid="home-hero">
    <div className="relative rounded-2xl overflow-hidden min-h-[640px] lg:min-h-[720px] flex items-end">
      <img src={home.hero_image} alt="Todd Coconato" className="absolute inset-0 w-full h-full object-cover object-[25%_center]" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A1C55] via-[#0A1C55]/30 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-l from-black/70 via-black/20 to-transparent hidden lg:block" />
      <div className="absolute inset-0 bg-black/40 lg:hidden" />
      <div className="relative w-full p-6 sm:p-10 lg:p-12 flex flex-col lg:items-end gap-10">
        <Reveal className="max-w-2xl lg:max-w-[46%]">
          <span className="eyebrow">The Transformed Mind Life with Todd Coconato</span>
          <h1 className="h1 !text-5xl sm:!text-6xl lg:!text-[76px]" data-testid="hero-headline">{home.hero_headline}</h1>
          <p className="mt-6 text-lg sm:text-xl text-slate-100 max-w-2xl leading-relaxed" data-testid="hero-subheadline">{home.hero_sub}</p>
          <div className="mt-9 flex flex-wrap gap-3">
            <Link to="/register" className="btn-gold" data-testid="hero-start-btn">Start Your Transformation</Link>
            <a href="https://toddcoconato.com/app/" target="_blank" rel="noopener noreferrer" className="btn-gold" data-testid="hero-app-btn"><Smartphone size={16} /> Download Our App</a>
            <Link to="/videos" className="btn-outline" data-testid="hero-explore-btn">Free Discipleship Videos</Link>
            <Link to="/podcast" className="btn-outline" data-testid="hero-podcast-btn"><Mic size={16} /> Podcast</Link>
            <Link to="/partnership" className="btn-ghost-gold" data-testid="hero-member-btn">Become a Partner <ArrowRight size={16} /></Link>
          </div>
          <a href="https://toddcoconato.com/app/" target="_blank" rel="noopener noreferrer" className="hidden lg:inline-flex items-center gap-4 mt-8 p-3 pr-6 rounded-2xl bg-black/50 backdrop-blur-md border border-[#D4AF37]/40 hover:border-[#D4AF37] transition-colors" data-testid="hero-app-qr">
            <img src="/images/app-qr.svg" alt="QR code to download the Todd Coconato app" className="w-24 h-24 rounded-lg bg-white p-1" />
            <span><span className="block text-xs uppercase tracking-widest text-gold-light">Get the app</span><span className="block text-white font-semibold mt-1">Scan with your phone<br />to download our app</span></span>
          </a>
        </Reveal>
        {featured && (
          <Reveal delay={0.3} className="w-full sm:w-80 shrink-0 lg:absolute lg:left-12 lg:bottom-12">
            <span className="eyebrow !mb-3">Featured teaching</span>
            <Link to={`/videos/${featured.slug}`} className="group relative block rounded-xl overflow-hidden aspect-video border border-white/20" data-testid="hero-featured-video">
              <img src={featured.thumbnail} alt="" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
              <p className="absolute left-4 bottom-4 right-28 text-sm font-semibold text-white line-clamp-2">{featured.title}</p>
              <span className="absolute right-3 bottom-3 flex items-center gap-2 bg-white/15 backdrop-blur px-4 py-2 rounded-full text-sm text-white"><Play size={14} fill="currentColor" />Watch</span>
            </Link>
          </Reveal>
        )}
      </div>
    </div>
  </section>
);

const SeriesStrip = ({ categories }) => (
  <section className="bg-black py-20 overflow-hidden" data-testid="series-strip">
    <div className="flex gap-12 overflow-x-auto no-scrollbar px-6 lg:px-[10vw] snap-x">
      {categories.map((c) => {
        const words = c.name.split(" ");
        const last = words.pop();
        return (
          <Link key={c.id} to={`/videos?category=${encodeURIComponent(c.name)}`} className="group shrink-0 w-52 text-center snap-start" data-testid={`series-${c.slug}`}>
            <p className="text-[10px] tracking-[0.3em] text-slate-400 font-semibold">TODD COCONATO</p>
            <p className="font-serif uppercase text-white leading-[0.9] mt-2 text-lg tracking-tight">{words.join(" ")}</p>
            <p className="font-serif uppercase font-extrabold text-white text-4xl leading-none tracking-[-0.04em] group-hover:text-gold-light transition-colors">{last}</p>
          </Link>
        );
      })}
    </div>
  </section>
);

const Pillars = () => (
  <section className="bg-white text-black py-24" data-testid="pillars-section">
    <div className="container-x grid lg:grid-cols-2 gap-14 items-center">
      <Reveal>
        <p className="font-accent text-xs uppercase tracking-[0.12em] flex items-center gap-2 mb-8"><span className="w-1.5 h-1.5 rounded-full bg-black" />Pillars of a transformed life</p>
        <ul>
          {PILLARS.map((p) => (
            <li key={p} className="font-serif font-semibold text-5xl sm:text-6xl lg:text-7xl tracking-[-0.05em] leading-[0.98] text-black hover:text-[#B8860B] transition-colors">{p}</li>
          ))}
        </ul>
      </Reveal>
      <Reveal delay={0.2}><img src="/images/todd-4707.jpg" alt="Todd Coconato" className="w-full aspect-[4/5] object-cover rounded-2xl" /></Reveal>
    </div>
  </section>
);

const Intro = ({ home }) => (
  <section className="py-28 bg-black" data-testid="intro-section">
    <div className="container-x grid lg:grid-cols-[1fr_1.2fr] gap-14 items-center">
      <Reveal><img src="/images/todd-4739.jpg" alt="Todd Coconato portrait" className="w-full aspect-square object-cover rounded-2xl" /></Reveal>
      <Reveal delay={0.15}>
        <span className="eyebrow">{home.intro_eyebrow}</span>
        <h2 className="h2">{home.intro_title}</h2>
        <p className="lead mt-6">{home.intro_body}</p>
        <Link to="/about" className="btn-outline mt-9" data-testid="intro-about-btn">Meet Todd Coconato <ArrowRight size={16} /></Link>
      </Reveal>
    </div>
  </section>
);

const Programs = ({ courses, programs }) => (
  <section className="py-24 bg-[#05070F]" data-testid="programs-section">
    <div className="container-x">
      <Reveal><span className="eyebrow">Programs</span><h2 className="h2 max-w-3xl">Courses and coaching for every stage of your journey.</h2></Reveal>
      <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {[...courses.map((c) => ({ ...c, to: `/courses/${c.slug}`, img: c.cover_image, kind: "Course" })),
          ...programs.map((p) => ({ ...p, to: "/coaching", img: p.image, kind: "Coaching" }))].slice(0, 6).map((p, i) => (
          <Reveal key={p.id} delay={i * 0.06}>
            <Link to={p.to} className="group block relative rounded-2xl overflow-hidden aspect-[4/5]" data-testid={`program-card-${p.slug}`}>
              <img src={p.img} alt="" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
              <div className="absolute bottom-0 p-7">
                <div className="flex gap-2 mb-3"><span className="text-[10px] uppercase tracking-widest bg-white text-black px-2.5 py-1 rounded-full">{p.kind}</span>{p.access && <AccessBadge access={p.access} />}</div>
                <h3 className="font-serif text-2xl font-semibold tracking-tight text-white">{p.title}</h3>
                <p className="mt-2 text-sm text-slate-300 line-clamp-2">{p.description?.split("\n")[0]}</p>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Books = ({ books }) => !books.length ? null : (
  <section className="py-24 bg-[#05070F]" data-testid="home-books-section">
    <div className="container-x">
      <Reveal><span className="eyebrow">Books by Todd</span><h2 className="h2 max-w-3xl">Words to strengthen your faith and sharpen your discernment.</h2></Reveal>
      <div className="grid md:grid-cols-3 gap-8 mt-12">
        {books.map((b, i) => (
          <Reveal key={b.id} delay={i * 0.1}>
            <div className="card-lux p-6 sm:p-8 flex flex-col gap-6 items-center h-full" data-testid={`home-book-${b.slug}`}>
              <img src={b.image} alt={b.title} className="w-40 sm:w-48 shrink-0 rounded-md shadow-[0_20px_50px_rgba(0,0,0,0.6)] transition-transform duration-500 hover:-translate-y-1 hover:rotate-[-1deg]" />
              <div className="flex flex-col flex-1 w-full">
                <h3 className="font-serif text-2xl text-white">{b.title}</h3>
                <p className="text-sm text-slate-400 mt-3 flex-1">{b.description}</p>
                <a href={b.amazon_url} target="_blank" rel="noopener noreferrer sponsored" className="btn-gold mt-6 self-start" data-testid={`home-book-buy-${b.slug}`}>Get the book <ArrowRight size={16} /></a>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Testimonials = ({ items }) => (
  <section className="py-24 bg-black" data-testid="testimonials-section">
    <div className="container-x">
      <Reveal><span className="eyebrow">Stories of transformation</span></Reveal>
      {items.length ? (
        <div className="grid md:grid-cols-3 gap-6 mt-8">
          {items.map((t) => (
            <div key={t.id} className="card-lux p-8"><Quote className="text-[#D4AF37]" /><p className="mt-5 text-slate-200 leading-relaxed">{t.quote}</p><p className="mt-6 text-sm text-white font-semibold">{t.name}</p><p className="text-xs text-slate-500">{t.role}</p></div>
          ))}
        </div>
      ) : (
        <div className="mt-4 flex flex-col md:flex-row md:items-end justify-between gap-6">
          <h2 className="h2 max-w-2xl">Has God used these teachings to change your life?</h2>
          <Link to="/contact" className="btn-gold" data-testid="share-story-btn">Share your story</Link>
        </div>
      )}
    </div>
  </section>
);

export default function Home() {
  const { settings } = useSite();
  const [d, setD] = useState({ videos: [], courses: [], programs: [], categories: [], testimonials: [], products: [] });
  useSEO({ jsonLd: { "@context": "https://schema.org", "@type": "Organization", name: "The Transformed Mind Life", founder: { "@type": "Person", name: "Todd Coconato" }, url: window.location.origin } });
  useEffect(() => {
    Promise.all(["videos", "courses", "programs", "categories", "testimonials", "products"].map((c) => api.get(`/content/${c}`).then((r) => r.data).catch(() => [])))
      .then(([videos, courses, programs, categories, testimonials, products]) => setD({ videos, courses, programs, categories, testimonials, products }));
  }, []);
  const home = settings?.home || { hero_headline: "TRANSFORM YOUR MIND. TRANSFORM YOUR LIFE.", hero_sub: "Break limiting beliefs. Renew your thinking. Strengthen your faith. Unlock your God-given potential.", hero_image: "/images/todd-stadium.jpg", intro_eyebrow: "Romans 12:2", intro_title: "Be transformed by the renewing of your mind.", intro_body: "", cta_title: "Your transformation starts with a single renewed thought." };
  const fids = home.featured_video_ids || [];
  const featuredVideos = fids.length ? d.videos.filter((v) => fids.includes(v.id)) : d.videos.filter((v) => v.featured);
  const heroVideo = featuredVideos[0] || d.videos[0];
  const rowVideos = (featuredVideos.length > 1 ? featuredVideos : d.videos).filter((v) => v.id !== heroVideo?.id).slice(0, 8);
  const fcids = home.featured_course_ids || [];
  const courses = fcids.length ? d.courses.filter((c) => fcids.includes(c.id)) : d.courses;
  return (
    <div data-testid="home-page">
      <Hero home={home} featured={heroVideo} />
      <SeriesStrip categories={d.categories} />
      <Pillars />
      <Intro home={home} />
      <Books books={d.products.filter((p) => p.category === "Books by Todd")} />
      <section className="py-20 bg-black"><div className="container-x">
        <Reveal><span className="eyebrow">Featured teachings</span></Reveal>
        <VideoRow title="Start watching" videos={rowVideos} testid="featured-videos-row" />
        <Link to="/videos" className="btn-outline" data-testid="home-library-btn">Open the full library <ArrowRight size={16} /></Link>
      </div></section>
      <Programs courses={courses} programs={d.programs} />
      <Testimonials items={d.testimonials} />
      <section className="py-24 bg-[#05070F]" data-testid="home-partnership">
        <div className="container-x">
          <Reveal className="text-center mb-14"><span className="eyebrow justify-center">Partnership</span><h2 className="h2">Partner with the message of the transformed mind.</h2></Reveal>
          <PartnerCards />
        </div>
      </section>
      <section className="px-3 sm:px-6 lg:px-[8vw] pt-24 bg-black">
        <div className="relative rounded-2xl overflow-hidden">
          <img src="/images/todd-4691.jpg" alt="" className="absolute inset-0 w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A1C55] via-[#0A1C55]/85 to-transparent" />
          <div className="relative p-8 sm:p-14 lg:p-20 max-w-2xl">
            <h2 className="h2">{home.cta_title}</h2>
            <p className="lead mt-5">Get new teachings, devotionals and event announcements in your inbox.</p>
            <div className="mt-8"><NewsletterForm /></div>
          </div>
        </div>
      </section>
    </div>
  );
}
